import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const WhatsAppSidebar = ({
    chats,
    activeChat,
    loadingChats,
    searchQuery,
    setSearchQuery,
    sidebarOpen,
    setSidebarOpen,
    onAddContactClick,
    onSyncClick,
    onSelectChat,
    onShowUserInfo,
    getPhotoUrl,
    getCountryFlag,
    getCountryFromWhatsAppId,
    t
}) => {
    const [visibleCount, setVisibleCount] = useState(20);

    // Reset visible count when search query changes
    useEffect(() => {
        setVisibleCount(20);
    }, [searchQuery]);

    const filteredChats = useMemo(() => {
        const query = searchQuery.toLowerCase().trim();
        if (!query) return chats;
        return chats.filter(chat => {
            const name = chat.name || chat.id.split('@')[0];
            return name.toLowerCase().includes(query) || chat.id.includes(query);
        });
    }, [chats, searchQuery]);

    const visibleChats = useMemo(() => {
        return filteredChats.slice(0, visibleCount);
    }, [filteredChats, visibleCount]);

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop - clientHeight < 50) {
            setVisibleCount(prev => Math.min(prev + 20, filteredChats.length));
        }
    };

    return (
        <div className={`chatbot-sidebar-wrapper ${sidebarOpen ? "panel-open" : "panel-closed"}`}>
            {/* Sidebar Rail */}
            <div className="sidebar-rail d-flex flex-column justify-content-between align-items-center py-3">
                <div className="rail-top d-flex flex-column align-items-center w-100">
                    <a href="/" className="rail-logo-wrapper mb-3 d-block">
                        <img
                            src={sidebarOpen ? "/assets/images/logo/logo.png" : "/assets/images/logo/logosmall.png"}
                            alt={t("alarabeyya")}
                            className="rail-logo"
                            style={{ width: sidebarOpen ? '120px' : '32px', transition: 'width 0.3s ease' }}
                        />
                    </a>
                    <button
                        type="button"
                        className="rail-btn hamburger-btn btn btn-link text-decoration-none d-flex align-items-center"
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        title={sidebarOpen ? t("Pages.Chatbot.CloseMenu") : t("Pages.Chatbot.OpenMenu")}
                    >
                        <i className="fal fa-bars"></i>
                        {sidebarOpen && <span className="rail-btn-text mr-2">{t("Pages.Chatbot.CloseMenu")}</span>}
                    </button>
                    <button
                        type="button"
                        className="rail-btn new-chat-btn btn btn-link text-decoration-none d-flex align-items-center"
                        onClick={onAddContactClick}
                        title={t("Pages.WhatsApp.addContact")}
                    >
                        <i className="fal fa-user-plus"></i>
                        {sidebarOpen && <span className="rail-btn-text mr-2">{t("Pages.WhatsApp.addContact")}</span>}
                    </button>
                    <button
                        type="button"
                        className="rail-btn btn btn-link text-decoration-none d-flex align-items-center"
                        onClick={onSyncClick}
                        title={t("Pages.WhatsApp.sync")}
                    >
                        <i className="fal fa-sync"></i>
                        {sidebarOpen && <span className="rail-btn-text mr-2">{t("Pages.WhatsApp.sync")}</span>}
                    </button>

                    {/* Link to User/Control */}
                    <Link
                        to="/User/Control"
                        className="rail-btn btn btn-link text-decoration-none d-flex align-items-center"
                        title={t("Pages.UserControl.title")}
                    >
                        <i className="fal fa-user-cog"></i>
                        {sidebarOpen && <span className="rail-btn-text mr-2">{t("Pages.UserControl.title")}</span>}
                    </Link>
                </div>
            </div>

            {/* Detailed Panel containing Chat list */}
            {sidebarOpen && (
                <div className="sidebar-panel bg-white border-left h-100">
                    <div className="panel-tab-content history-tab-content d-flex flex-column h-100">
                        <div className="panel-header d-flex align-items-center justify-content-between p-3 border-bottom">
                            <h3 className="panel-title h6 font-weight-bold m-0 d-flex align-items-center">
                                <i className="fab fa-whatsapp ml-2" style={{ color: '#00a884', fontSize: '18px' }}></i>
                                <span>{t("Pages.WhatsApp.title")}</span>
                            </h3>
                        </div>

                        <div className="panel-search-container p-3 position-relative">
                            <i className="fal fa-search panel-search-icon" style={{ position: 'absolute', right: '25px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}></i>
                            <input
                                type="text"
                                className="panel-search-input form-control form-control-sm pr-5"
                                placeholder={t("Pages.WhatsApp.searchPlaceholder")}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>

                        <div
                            className="panel-scroll-content history-list-scroll flex-grow-1 p-3 overflow-auto"
                            onScroll={handleScroll}
                        >
                            {loadingChats ? (
                                <div className="text-center py-5">
                                    <i className="fa-spin fas fa-spinner text-success h3"></i>
                                    <p className="mt-2 text-muted">{t("Loading")}</p>
                                </div>
                            ) : filteredChats.length === 0 ? (
                                <div className="text-center py-5 text-muted">
                                    <i className="fal fa-comments h1 text-muted opacity-50 mb-2"></i>
                                    <p>{t("NoInfo")}</p>
                                </div>
                            ) : (
                                visibleChats.map(chat => {
                                    const chatName = chat.name || chat.id.split('@')[0];
                                    const initials = chatName.substring(0, 1).toUpperCase();
                                    const isActive = activeChat && activeChat.id === chat.id;
                                    const photoUrl = chat.systemUser && chat.systemUser.photo ? getPhotoUrl(chat.systemUser.photo) : (chat.avatar || chat.image || null);
                                    const countryCode = chat.systemUser && chat.systemUser.country ? chat.systemUser.country.toLowerCase() : getCountryFromWhatsAppId(chat.id);

                                    const lastMsg = chat.lastMessage;
                                    let lastMsgText = "";
                                    if (lastMsg) {
                                        if (lastMsg.typeMessage === 'textMessage') {
                                            lastMsgText = lastMsg.textMessage;
                                        } else if (lastMsg.typeMessage === 'imageMessage') {
                                            lastMsgText = `📷 ${t("Pages.WhatsApp.document", "صورة")}`;
                                        } else if (lastMsg.typeMessage === 'videoMessage') {
                                            lastMsgText = `🎥 ${t("Pages.WhatsApp.document", "فيديو")}`;
                                        } else if (lastMsg.typeMessage === 'audioMessage') {
                                            lastMsgText = `🎵 ${t("Pages.WhatsApp.document", "صوت")}`;
                                        } else if (lastMsg.typeMessage === 'documentMessage') {
                                            lastMsgText = `📄 ${t("Pages.WhatsApp.document", "مستند")}`;
                                        } else {
                                            lastMsgText = lastMsg.textMessage || `💬 ${t("Pages.WhatsApp.message", "رسالة")}`;
                                        }
                                    }

                                    return (
                                        <div
                                            key={chat.id}
                                            className={`history-drawer-item ${isActive ? "active" : ""}`}
                                            onClick={() => onSelectChat(chat)}
                                        >
                                            <div
                                                className="whatsapp-avatar clickable-avatar ml-2"
                                                title={t("Pages.WhatsApp.showUserInfo")}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onShowUserInfo(chat);
                                                }}
                                            >
                                                {photoUrl ? (
                                                    <img src={photoUrl} alt={chatName} className="whatsapp-avatar-img" />
                                                ) : (
                                                    initials
                                                )}
                                            </div>
                                            <div className="history-item-content text-right overflow-hidden flex-grow-1">
                                                <div className="history-item-title font-weight-bold text-truncate text-dark d-flex align-items-center flex-wrap gap-1">
                                                    {chatName}
                                                    {countryCode && (
                                                        <img
                                                            src={process.env.PUBLIC_URL + getCountryFlag(countryCode)}
                                                            className="country-flag-icon"
                                                            title={countryCode.toUpperCase()}
                                                            alt={countryCode.toUpperCase()}
                                                            onError={(e) => { e.target.style.display = 'none'; }}
                                                        />
                                                    )}
                                                </div>
                                                <div className="history-item-date small text-muted text-truncate mt-1 d-flex justify-content-between align-items-center">
                                                    <span className="text-truncate flex-grow-1 text-right">{lastMsgText || chat.id}</span>
                                                    {lastMsg && lastMsg.timestamp && (
                                                        <span className="last-msg-time mr-2" style={{ fontSize: '10px', color: '#94a3b8', flexShrink: 0 }}>
                                                            {new Date(lastMsg.timestamp * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WhatsAppSidebar;
